import React, { useState, useEffect } from 'react';
import { collection, addDoc, getDocs, doc, deleteDoc } from 'firebase/firestore';
import { firestore } from '../lib/firebaseConfig';
import BlockTitle from './BlockTitle';
import Button from './Button';
import FormInput from './FormInput';
import FormLabel from './FormLabel';
import ConfirmDialog from './ConfirmDialog';
import { useToast } from '../context/ToastContext';

const SURCHARGE_TYPES = [
  { value: 'surface', label: 'Korekta powierzchni' },
  { value: 'parking', label: 'Miejsce parkingowe' },
  { value: 'other', label: 'Inne' },
];

const formatPLN = (amount) =>
  new Intl.NumberFormat('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount) + ' zl';

const SurchargeSection = ({ investmentId, flatId }) => {
  const { showSuccess, showError } = useToast();
  const [surcharges, setSurcharges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [formData, setFormData] = useState({
    type: 'surface',
    amount: 0,
    description: '',
  });

  useEffect(() => {
    const fetch = async () => {
      if (!investmentId || !flatId) return;
      try {
        const snap = await getDocs(
          collection(firestore, `investments/${investmentId}/flats/${flatId}/surcharges`)
        );
        setSurcharges(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (e) {
        console.error('Error fetching surcharges:', e);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [investmentId, flatId]);

  const handleSave = async () => {
    const amount = parseFloat(formData.amount) || 0;
    if (amount === 0) {
      showError('Podaj kwote');
      return;
    }

    const typeLabel = SURCHARGE_TYPES.find(t => t.value === formData.type)?.label || formData.type;

    const data = {
      type: formData.type,
      typeLabel,
      amount,
      description: formData.description,
      createdAt: new Date().toISOString(),
    };

    try {
      const docRef = await addDoc(
        collection(firestore, `investments/${investmentId}/flats/${flatId}/surcharges`),
        data
      );
      setSurcharges([...surcharges, { id: docRef.id, ...data }]);
      showSuccess('Doplata dodana');
      setShowForm(false);
      setFormData({ type: 'surface', amount: 0, description: '' });
    } catch (e) {
      showError('Blad zapisu');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteDoc(doc(firestore, `investments/${investmentId}/flats/${flatId}/surcharges/${deleteTarget.id}`));
      setSurcharges(surcharges.filter(s => s.id !== deleteTarget.id));
      showSuccess('Doplata usunieta');
    } catch (e) {
      showError('Blad usuwania');
    }
    setDeleteTarget(null);
  };

  if (loading) return null;

  return (
    <div className="mt-[28px] bg-white rounded-[6px] border border-[var(--bright-gray)] p-5">
      <div className="flex justify-between items-center">
        <BlockTitle>Doplaty</BlockTitle>
        {!showForm && (
          <Button variant="bordered" className="text-xs" onClick={() => setShowForm(true)}>
            + Dodaj doplate
          </Button>
        )}
      </div>

      {surcharges.length === 0 && !showForm && (
        <p className="text-gray-400 text-sm mt-3">Brak doplat</p>
      )}

      {surcharges.length > 0 && (
        <table className="w-full mt-3 text-sm">
          <thead>
            <tr className="bg-[var(--lotion)] border-b border-[var(--bright-gray)]">
              <th className="text-left py-2 px-3">Typ</th>
              <th className="text-left py-2 px-3">Opis</th>
              <th className="text-right py-2 px-3">Kwota</th>
              <th className="text-left py-2 px-3">Data</th>
              <th className="py-2 px-3"></th>
            </tr>
          </thead>
          <tbody>
            {surcharges.map((s) => (
              <tr key={s.id} className="border-b border-[var(--bright-gray)]">
                <td className="py-2 px-3">{s.typeLabel}</td>
                <td className="py-2 px-3">{s.description}</td>
                <td className="py-2 px-3 text-right font-medium">{formatPLN(s.amount)}</td>
                <td className="py-2 px-3">{s.createdAt?.split('T')[0]}</td>
                <td className="py-2 px-3">
                  <button onClick={() => setDeleteTarget(s)} className="text-red-400 hover:text-red-600 text-xs">x</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showForm && (
        <div className="mt-3 p-4 bg-gray-50 rounded-lg">
          <div className="grid grid-cols-4 gap-3">
            <div>
              <FormLabel>Typ</FormLabel>
              <select
                className="w-full h-[38px] border border-[#D7D7D7] rounded-[6px] px-2 text-sm"
                value={formData.type}
                onChange={(e) => setFormData({...formData, type: e.target.value})}
              >
                {SURCHARGE_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div>
              <FormLabel>Kwota (PLN)</FormLabel>
              <FormInput
                type="number"
                step="0.01"
                value={formData.amount}
                onChange={(e) => setFormData({...formData, amount: e.target.value})}
              />
            </div>
            <div className="col-span-2">
              <FormLabel>Opis</FormLabel>
              <FormInput
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="np. Roznica powierzchni +0.5m2"
              />
            </div>
          </div>
          <div className="mt-3 flex gap-2">
            <Button variant="default" className="text-xs" onClick={handleSave}>Zapisz</Button>
            <Button variant="bordered" className="text-xs" onClick={() => setShowForm(false)}>Anuluj</Button>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={deleteTarget !== null}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
        title="Usun doplate"
        message={`Usunac doplate ${deleteTarget?.typeLabel || ''}?`}
        confirmText="Usun"
        cancelText="Anuluj"
        variant="danger"
      />
    </div>
  );
};

export default SurchargeSection;
